<script setup lang="ts">
import type { IMicrocreditCompanyCard } from '~/components/entity/microcredit/item/types'
import { useDateFormat } from '~/composables/useDateTimeFormat'
import type { IEntityPosition } from '~/components/entity/base-entity'
import { UiCardTop } from '~/components/ui/card'
import { Rating } from '~/components/ui/rating'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'

const props = withDefaults(
  defineProps<{
    item: IMicrocreditCompanyCard
    hideOrganizationDetails?: boolean
    position?: IEntityPosition
  }>(),
  {
    hideOrganizationDetails: false,
    position: undefined
  }
)

const isHighlighted = useEntityHighlight(props.item, props.position)
const formattedLicenseDate = computed(() => useDateFormat(props.item.license_date))

// The title may carry the search highlight (<em>); attributes need plain text
const plainTitle = computed(() => useStripTags(props.item.title))
</script>

<template>
  <article :class="[isHighlighted ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="isHighlighted" />
    <div class="EntityCardLayout">
      <div class="flex gap-4">
        <EntityOrganizationLogo
          v-if="!props.hideOrganizationDetails"
          :src="props.item.logo"
          :alt="plainTitle"
          :to="props.item.url"
          :highlighted="isHighlighted"
        />
        <div>
          <NuxtLinkLocale
            :to="props.item.url"
            :aria-label="$t('common.detailsAbout', { title: plainTitle })"
            class="inline-block hover:underline"
          >
            <Heading tag="h3" class="hit-title h6"><span v-hydrated-html="props.item.title" /></Heading>
          </NuxtLinkLocale>
          <p class="SecondaryTextHeadings">
            {{ $t('common.license') }} {{ props.item.license }} {{ $t('common.from') }}
            {{ formattedLicenseDate }}
          </p>
          <div v-if="props.item.ratings" class="flex items-center">
            <Rating :value="Number(props.item.ratings)" size="xs" />
            <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-y-2">
        <div v-if="props.item.credits_count">
          <NuxtLinkLocale
            :to="`${props.item.url}/credits`"
            class="text-sm text-blue-600"
            :aria-label="$t('common.credits_list.company_name', { title: plainTitle })"
            >{{ $t('common.credits') }}</NuxtLinkLocale
          >
          <div class="font-semibold">{{ props.item.credits_count }}</div>
        </div>
        <div v-if="props.item.deposits_count">
          <NuxtLinkLocale
            :to="`${props.item.url}/deposits`"
            class="text-sm text-blue-600"
            :aria-label="$t('common.deposits_list.company_name', { title: plainTitle })"
            >{{ $t('common.deposits') }}</NuxtLinkLocale
          >
          <div class="font-semibold">{{ props.item.deposits_count }}</div>
        </div>
      </div>
    </div>
  </article>
</template>
